import { useNavigate } from '@tanstack/react-router';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';

import CrossIcon from '@common/assets/icons/cross.svg?react';
import SendIcon from '@common/assets/icons/send.svg?react';
import { useTabbar } from '@common/providers/useTabbar';
import { BottomSheet } from '@common/ui/BottomSheet';
import { Button } from '@common/ui/buttons/Button';
import { Loader } from '@common/ui/Loader';
import { useEventEmit } from '@common/utils/events';
import { isRateLimitedError } from '@common/utils/fetcher';

import { loadAccountSettings } from '@entities/account';
import { useAuthStore } from '@entities/auth/stores/useAuthStore';
import { FiltersButton } from '@entities/dating/ui/FiltersButton';
import { ProfileCard } from '@entities/dating/ui/ProfileCard';
import { ProfileInfo } from '@entities/dating/ui/ProfileInfo';
import {
  deckCriteriaKey,
  deckCriteriaOf,
  isDeckRefusalStale,
  type MatchmakerFriend,
  refusalFromError,
  useMatchmakerDeckStore,
  useMatchmakerFriendStore,
  useMatchmakerModeStore,
  writeSwipeRemaining,
} from '@entities/matchmaker';

import { analytics } from '@services/AnalyticsService';
import { usePostMatchmakerSkip } from '@services/hooks/MatchmakerService';
import { type PostMatchmakerRecommendationResponse } from '@services/MatchmakerService';
import { type User } from '@services/UsersService';

import styles from './MatchmakerDeck.module.scss';
import { MatchmakerResult } from '../MatchmakerResult';
import { RecommendResultSheet } from '../RecommendResultSheet';
import { RecommendSheet } from '../RecommendSheet';
import { SwipeLimitResult } from '../SwipeLimitResult';

export type MatchmakerDeckProps = {
  friend: MatchmakerFriend;
  /** The height of everything above the card, in px. */
  topOffset: number;
};

type DeckSheet =
  | { kind: 'recommend'; card: User }
  | {
      kind: 'result';
      card: User;
      recommendation: PostMatchmakerRecommendationResponse;
    };

const VISIBLE_CARDS_COUNT = 2;

const RATE_LIMITED_NOTICE = {
  title: 'Слишком много действий',
  description: 'Попробуйте ещё раз чуть позже',
};

/**
 * The matchmaker's cards (board 3.1): the real feed card without a gesture,
 * a heart or a like prompt, and «Пропустить» / «Посоветовать другу» under it.
 */
export const MatchmakerDeck = ({ friend, topOffset }: MatchmakerDeckProps) => {
  const navigate = useNavigate();
  const emit = useEventEmit();
  const tabbar = useTabbar();
  // The slot's setter, without making it an effect dependency.
  const tabbarRef = useRef(tabbar);
  tabbarRef.current = tabbar;

  const setMode = useMatchmakerModeStore((state) => state.setMode);
  const clearFriendCity = useMatchmakerFriendStore(
    (state) => state.clearFriendCity,
  );
  const deck = useMatchmakerDeckStore();

  const criteria = useMemo(() => deckCriteriaOf(friend), [friend]);
  const criteriaKey = deckCriteriaKey(criteria);
  const isCurrentDeck = deck.criteriaKey === criteriaKey;
  const cards = isCurrentDeck ? deck.cards : [];
  const refusal = isCurrentDeck ? deck.refusal : null;
  const topCard = refusal ? undefined : cards[0];

  const [sheet, setSheet] = useState<DeckSheet | null>(null);
  const sharedCardIdRef = useRef<string | null>(null);
  // A refusal left from an earlier mount is not acted on again.
  const mountRefusalRef = useRef(refusal);
  const approvalStatus = useAuthStore(
    (state) => state.user?.approval_status?.status,
  );
  const approvalStatusRef = useRef(approvalStatus);

  const { trigger: triggerPostMatchmakerSkip } = usePostMatchmakerSkip();

  useEffect(() => {
    const { refusal: storedRefusal, clearRefusal } =
      useMatchmakerDeckStore.getState();

    const swipesRemaining = useAuthStore.getState().swipe_status?.remaining;

    if (storedRefusal && isDeckRefusalStale(storedRefusal, swipesRemaining)) {
      clearRefusal();
    }

    return () => {
      if (sharedCardIdRef.current) {
        useMatchmakerDeckStore.getState().removeCard(sharedCardIdRef.current);
      }

      tabbarRef.current.setBottomSheet(undefined);
    };
  }, []);

  useEffect(() => {
    void useMatchmakerDeckStore.getState().loadMore(criteria);
  }, [
    criteria,
    criteriaKey,
    cards.length,
    deck.isLoading,
    deck.isExhausted,
    refusal,
  ]);

  // Approved while the deck waits: ask again (the server checks approval
  // before it reserves any card).
  useEffect(() => {
    const previousApprovalStatus = approvalStatusRef.current;
    approvalStatusRef.current = approvalStatus;

    if (
      approvalStatus === 'approved' &&
      previousApprovalStatus !== 'approved' &&
      useMatchmakerDeckStore.getState().refusal?.kind === 'not_approved'
    ) {
      useMatchmakerDeckStore.getState().clearRefusal();
    }
  }, [approvalStatus]);

  useEffect(() => {
    if (!refusal || refusal === mountRefusalRef.current) return;

    if (refusal.kind === 'not_matchmaker') {
      void loadAccountSettings();
      setMode('dating');
    }

    if (refusal.kind === 'city') clearFriendCity();
  }, [clearFriendCity, refusal, setMode]);

  const handleSkip = async (card: User) => {
    const { removeCard, putBack, refuse } = useMatchmakerDeckStore.getState();

    removeCard(card.id);

    try {
      const { remaining } = await triggerPostMatchmakerSkip({
        candidate_user_id: card.id,
      });

      writeSwipeRemaining(remaining);
      analytics.trackMmSkip();
    } catch (error) {
      putBack(card);

      if (isRateLimitedError(error)) {
        emit('info', RATE_LIMITED_NOTICE);

        return;
      }

      const skipRefusal = refusalFromError(error);

      if (skipRefusal) {
        refuse(skipRefusal);

        return;
      }

      emit('error', {
        title: 'Ошибка действия',
        description: 'Попробуйте позже',
      });
    }
  };

  useEffect(() => {
    if (!topCard || sheet) {
      tabbarRef.current.setBottomSheet(undefined);

      return;
    }

    tabbarRef.current.setBottomSheet(
      <BottomSheet
        buttonDirection="row"
        buttons={[
          <Button
            testId="matchmaker-deck-skip-button"
            className={styles.deckButton}
            variant="negativeSecondary"
            onClick={() => void handleSkip(topCard)}
          >
            <CrossIcon />
            Пропустить
          </Button>,
          <Button
            testId="matchmaker-deck-recommend-button"
            className={styles.deckButton}
            variant="actionPrimary"
            onClick={() => setSheet({ kind: 'recommend', card: topCard })}
          >
            <SendIcon />
            Посоветовать другу
          </Button>,
        ]}
      />,
    );
  }, [topCard, sheet]);

  const handleShared = (
    card: User,
    recommendation: PostMatchmakerRecommendationResponse,
  ) => {
    sharedCardIdRef.current = card.id;
    setSheet({ kind: 'result', card, recommendation });
  };

  const handleDone = (card: User) => {
    sharedCardIdRef.current = null;
    useMatchmakerDeckStore.getState().removeCard(card.id);
    setSheet(null);
  };

  const renderState = () => {
    switch (refusal?.kind) {
      case 'swipe_limit':
        return <SwipeLimitResult resetTime={refusal.resetTime} />;
      case 'browse_limit':
        return (
          <MatchmakerResult
            testId="matchmaker-deck-browse-limit"
            icon3d="sand-clock.png"
            title="На сегодня всё"
            description={
              refusal.resetTime
                ? `Новые анкеты появятся через ${moment(refusal.resetTime).fromNow(true)}`
                : undefined
            }
          />
        );
      case 'not_approved':
        return (
          <MatchmakerResult
            testId="matchmaker-deck-not-approved"
            icon3d="sand-clock.png"
            title="Листать анкеты можно после проверки вашей анкеты"
          />
        );
      case 'not_available':
      case 'not_matchmaker':
      case 'feature_disabled':
        return (
          <MatchmakerResult
            testId="matchmaker-deck-not-available"
            icon3d="sad.png"
            title="Режим свахи сейчас недоступен"
          />
        );
      case 'failed':
        return (
          <MatchmakerResult
            testId="matchmaker-deck-failed"
            icon3d="sad.png"
            title="Ошибка загрузки анкет"
            description="Попробуйте позже"
            button={
              <Button
                testId="matchmaker-deck-retry-button"
                onClick={() => useMatchmakerDeckStore.getState().clearRefusal()}
              >
                Повторить
              </Button>
            }
          />
        );
      case 'city':
        return null;
    }

    if (cards.length > 0) return null;

    if (isCurrentDeck && deck.isExhausted && !deck.isLoading) {
      return (
        <MatchmakerResult
          testId="matchmaker-deck-empty"
          icon3d="not-found.png"
          title="Подходящих анкет пока нет"
          description="Попробуйте другой город или возраст"
        />
      );
    }

    return <Loader />;
  };

  return (
    <div className={styles.matchmakerDeck} data-test-id="matchmaker-deck">
      <div
        className={styles.cards}
        style={{
          height: `calc(100vh - var(--tabbar-height) - ${topOffset}px - 8px)`,
        }}
      >
        {renderState() ??
          cards.slice(0, VISIBLE_CARDS_COUNT).map((card, index) => (
            <ProfileCard
              key={card.id}
              variant="matchmaker"
              profile={card}
              style={{
                position: 'absolute',
                zIndex: VISIBLE_CARDS_COUNT - index,
                pointerEvents: index === 0 ? 'auto' : 'none',
              }}
              filtersButton={
                <FiltersButton
                  aria-label="Кого сосватаем?"
                  onClick={() => navigate({ to: '/matchmaker/friend' })}
                  style={{
                    background: 'var(--bg-secondary-on-dark-transparent)',
                  }}
                />
              }
            />
          ))}
      </div>

      {topCard && <ProfileInfo profile={topCard} variant="matchmaker" />}

      {sheet?.kind === 'recommend' && (
        <RecommendSheet
          card={sheet.card}
          friend={friend}
          onClose={() => setSheet(null)}
          onSent={(recommendation) => handleShared(sheet.card, recommendation)}
        />
      )}

      {sheet?.kind === 'result' && (
        <RecommendResultSheet
          recommendation={sheet.recommendation}
          onDone={() => handleDone(sheet.card)}
        />
      )}
    </div>
  );
};
